import { useState } from "react";
import { authFetch } from "../api";

/**
 * Add / edit a cooling center.
 * Coordinates can be supplied via:
 *   1. Address geocoding  (calls backend /geocode)
 *   2. Manual lat / lng
 *   3. A Google Maps link  (coordinates extracted client-side from the URL)
 */
export default function CenterForm({ center, onSave, onClose }) {
  const apiBase = process.env.REACT_APP_API_URL;
  const isNew   = !center;

  const [fields, setFields] = useState({
    name:      center?.name      ?? "",
    address:   center?.address   ?? "",
    hours:     center?.hours     ?? "",
    capacity:  center?.capacity  ?? "",
    phone:     center?.phone     ?? "",
    amenities: center?.amenities ?? "",
    is_open:   center?.is_open   ?? true,
  });

  const [lat, setLat]         = useState(center?.lat  != null ? String(center.lat)  : "");
  const [lng, setLng]         = useState(center?.lng  != null ? String(center.lng)  : "");
  const [mapsUrl, setMapsUrl] = useState("");
  const [coordTab, setCoordTab] = useState("address"); // "address" | "manual" | "link"

  const [geocoding, setGeocoding] = useState(false);
  const [geocodeMsg, setGeocodeMsg] = useState(null); // { ok, text }
  const [saving, setSaving]       = useState(false);

  const set = (key) => (e) =>
    setFields((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  // ── 1. Geocode via address ────────────────────────────────────────────────
  const handleGeocode = async () => {
    if (!fields.address.trim()) { setGeocodeMsg({ ok: false, text: "Enter an address first." }); return; }
    setGeocoding(true);
    setGeocodeMsg(null);
    try {
      const res = await authFetch(`${apiBase}/geocode?address=${encodeURIComponent(fields.address)}`);
      if (!res.ok) throw new Error("Could not geocode this address.");
      const data = await res.json();
      setLat(String(data.lat));
      setLng(String(data.lng));
      setGeocodeMsg({ ok: true, text: `Found: ${data.formatted_address}` });
    } catch (err) {
      setGeocodeMsg({ ok: false, text: err.message });
    } finally {
      setGeocoding(false);
    }
  };

  // ── 3. Extract from Google Maps URL ──────────────────────────────────────
  const extractFromMapsUrl = (url) => {
    // Handles formats like:
    //   .../@28.6139,77.2090,...
    //   ...?q=28.6139,77.2090
    //   ...?ll=28.6139,77.2090
    const atMatch  = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
    const qMatch   = url.match(/[?&](?:q|ll)=(-?\d+\.\d+),(-?\d+\.\d+)/);
    const match = atMatch || qMatch;
    if (match) {
      setLat(match[1]);
      setLng(match[2]);
      setGeocodeMsg({ ok: true, text: `Extracted: ${match[1]}, ${match[2]}` });
    } else {
      setGeocodeMsg({ ok: false, text: "Could not extract coordinates from this link." });
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsedLat = parseFloat(lat);
    const parsedLng = parseFloat(lng);
    if (isNaN(parsedLat) || isNaN(parsedLng)) {
      alert("Coordinates are required. Use the geocode button, enter them manually, or paste a Maps link.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        id: isNew ? Date.now().toString() : center.id,
        ...fields,
        capacity: fields.capacity !== "" ? Number(fields.capacity) : null,
        lat: parsedLat,
        lng: parsedLng,
      };

      const method = isNew ? "POST" : "PUT";
      const url    = isNew ? `${apiBase}/centers` : `${apiBase}/centers/${center.id}`;

      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to save center");
      onSave(isNew ? "Center added successfully!" : "Center updated successfully!");
      onClose();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const hasCoords = lat !== "" && lng !== "";

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(0,0,0,0.4)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: 16, overflowY: "auto",
    }}>
      <div style={{
        background: "#fff", borderRadius: 14,
        width: "100%", maxWidth: 540,
        padding: 24, maxHeight: "90vh", overflowY: "auto",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            {isNew ? "Add new center" : `Edit: ${center.name}`}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#888" }}>×</button>
        </div>

        <form onSubmit={handleSubmit}>

          {/* ── Basic fields ─────────────────────────────────────── */}
          {[
            { label: "Name *", key: "name", type: "text", required: true },
            { label: "Address *", key: "address", type: "text", required: true },
            { label: "Hours", key: "hours", type: "text" },
            { label: "Capacity", key: "capacity", type: "number" },
            { label: "Phone", key: "phone", type: "text" },
            { label: "Amenities", key: "amenities", type: "text" },
          ].map(({ label, key, type, required }) => (
            <div key={key} style={{ marginBottom: 14 }}>
              <label style={labelStyle}>{label}</label>
              <input
                type={type}
                value={fields[key]}
                onChange={set(key)}
                required={required}
                style={inputStyle}
              />
            </div>
          ))}

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }}>
              <input type="checkbox" checked={fields.is_open} onChange={set("is_open")} />
              Is Open
            </label>
          </div>

          {/* ── Coordinates ─────────────────────────────────────── */}
          <div style={{ border: "1px solid #e0e0e0", borderRadius: 10, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: "#333" }}>
              Coordinates {hasCoords
                ? <span style={{ color: "#1a9e4a", fontWeight: 400 }}>✓ {lat}, {lng}</span>
                : <span style={{ color: "#cc3300", fontWeight: 400 }}>— not set</span>}
            </div>

            {/* Tab switcher */}
            <div style={{ display: "flex", gap: 0, marginBottom: 14, borderRadius: 8, overflow: "hidden", border: "1px solid #ddd" }}>
              {[
                { id: "address", label: "From address" },
                { id: "manual",  label: "Enter manually" },
                { id: "link",    label: "Maps link" },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => { setCoordTab(id); setGeocodeMsg(null); }}
                  style={{
                    flex: 1, padding: "7px 4px", fontSize: 12, fontWeight: 600, border: "none",
                    cursor: "pointer",
                    background: coordTab === id ? "#0093dd" : "#f5f5f5",
                    color: coordTab === id ? "#fff" : "#555",
                    borderRight: id !== "link" ? "1px solid #ddd" : "none",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Tab: From address */}
            {coordTab === "address" && (
              <div>
                <p style={{ fontSize: 12, color: "#666", marginBottom: 10 }}>
                  Uses the address entered above. Try adding city/state if it fails.
                </p>
                <button
                  type="button"
                  onClick={handleGeocode}
                  disabled={geocoding}
                  style={{ ...inputBtnStyle, background: geocoding ? "#aaa" : "#0093dd" }}
                >
                  {geocoding ? "Looking up…" : "Geocode address"}
                </button>
              </div>
            )}

            {/* Tab: Manual */}
            {coordTab === "manual" && (
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Latitude</label>
                  <input
                    type="number" step="any"
                    value={lat}
                    onChange={e => setLat(e.target.value)}
                    placeholder="e.g. 28.6139"
                    style={inputStyle}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Longitude</label>
                  <input
                    type="number" step="any"
                    value={lng}
                    onChange={e => setLng(e.target.value)}
                    placeholder="e.g. 77.2090"
                    style={inputStyle}
                  />
                </div>
              </div>
            )}

            {/* Tab: Maps link */}
            {coordTab === "link" && (
              <div>
                <label style={labelStyle}>Paste a Google Maps link</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="url"
                    value={mapsUrl}
                    onChange={e => setMapsUrl(e.target.value)}
                    placeholder="https://maps.google.com/..."
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => extractFromMapsUrl(mapsUrl)}
                    style={{ ...inputBtnStyle, whiteSpace: "nowrap" }}
                  >
                    Extract
                  </button>
                </div>
              </div>
            )}

            {/* Feedback message */}
            {geocodeMsg && (
              <div style={{
                marginTop: 10, fontSize: 12, padding: "6px 10px", borderRadius: 6,
                background: geocodeMsg.ok ? "#e8f7ed" : "#fdecea",
                color: geocodeMsg.ok ? "#1a6e35" : "#8B2020",
                border: `1px solid ${geocodeMsg.ok ? "#a8ddb8" : "#f5c6c2"}`,
              }}>
                {geocodeMsg.text}
              </div>
            )}
          </div>

          {/* ── Actions ─────────────────────────────────────────── */}
          <div style={{ display: "flex", gap: 12 }}>
            <button
              type="submit"
              disabled={saving}
              style={{ padding: "10px 20px", background: "#0093dd", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
            >
              {saving ? "Saving…" : (isNew ? "Add Center" : "Update Center")}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{ padding: "10px 20px", background: "#f0f0ee", color: "#333", border: "none", borderRadius: 6, cursor: "pointer" }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const labelStyle = { display: "block", marginBottom: 4, fontSize: 13, color: "#444", fontWeight: 500 };
const inputStyle = { width: "100%", padding: "8px 10px", border: "1px solid #ccc", borderRadius: 6, fontSize: 14, boxSizing: "border-box" };
const inputBtnStyle = { padding: "8px 16px", background: "#0093dd", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 };
